// src/mobile_pages/const/Photo_view.jsx
import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ChevronLeft,
  ChevronDown,
  Save,
  Trash2,
  Edit2,
  Image,
  Calendar,
  User,
  FileText,
  X,
  ZoomIn,
  ZoomOut
} from "lucide-react";
import { getPhotoDetail, deletePhoto, updatePhoto } from "../../api/const";

const Photo_view = () => {
  const { photoId } = useParams();
  const navigate = useNavigate();
  const [photo, setPhoto] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState({
    title: "",
    description: "",
    date: "",
  });

  const [expandedSections, setExpandedSections] = useState({
    info: true,
    description: true,
  });

  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const lastDistance = useRef(0);
  const lastPosition = useRef({ x: 0, y: 0 });
  const isDragging = useRef(false);
  const imageContainerRef = useRef(null);

  useEffect(() => {
    if (photoId) {
      getPhotoDetail(photoId)
        .then((data) => {
          setPhoto(data);
          setForm({
            title: data.title || "",
            description: data.description || "",
            date: data.date ? data.date.split("T")[0] : "",
          });
          setLoading(false);
        })
        .catch((err) => {
          console.error("사진 불러오기 실패:", err);
          setLoading(false);
        });
    }
  }, [photoId]);

  const toggleSection = (key) => {
    setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleDelete = async () => {
    if (!window.confirm("정말 삭제하시겠습니까?")) return;
    try {
      await deletePhoto(photoId);
      alert("사진이 삭제되었습니다.");
      navigate(-1);
    } catch (error) {
      console.error("삭제 실패:", error);
      alert("삭제에 실패했습니다.");
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await updatePhoto(photoId, form);
      setPhoto({ ...photo, ...updated });
      setIsEditing(false);
      alert("사진 정보가 수정되었습니다.");
    } catch (error) {
      console.error("수정 실패:", error);
      alert("수정에 실패했습니다.");
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    if (isEditing) {
      if (window.confirm("작성 중인 내용이 저장되지 않습니다. 나가시겠습니까?")) {
        navigate(-1);
      }
    } else {
      navigate(-1);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("ko-KR");
  };

  const openViewer = () => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
    setIsViewerOpen(true);
  };

  const closeViewer = () => {
    setIsViewerOpen(false);
    setScale(1);
    setPosition({ x: 0, y: 0 });
  };

  const handleZoomIn = () => {
    setScale((prev) => Math.min(prev + 0.5, 4));
  };

  const handleZoomOut = () => {
    setScale((prev) => {
      const newScale = Math.max(prev - 0.5, 1);
      if (newScale === 1) setPosition({ x: 0, y: 0 });
      return newScale;
    });
  };

  const getDistance = (touches) => {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const handleTouchStart = (e) => {
    if (e.touches.length === 2) {
      lastDistance.current = getDistance(e.touches);
    } else if (e.touches.length === 1 && scale > 1) {
      isDragging.current = true;
      lastPosition.current = {
        x: e.touches[0].clientX - position.x,
        y: e.touches[0].clientY - position.y,
      };
    }
  };

  const handleTouchMove = (e) => {
    if (e.touches.length === 2) {
      const distance = getDistance(e.touches);
      const delta = distance - lastDistance.current;
      const scaleChange = delta * 0.01;
      setScale((prev) => {
        const newScale = Math.min(Math.max(prev + scaleChange, 1), 4);
        if (newScale === 1) setPosition({ x: 0, y: 0 });
        return newScale;
      });
      lastDistance.current = distance;
    } else if (e.touches.length === 1 && isDragging.current && scale > 1) {
      const newX = e.touches[0].clientX - lastPosition.current.x;
      const newY = e.touches[0].clientY - lastPosition.current.y;
      const maxOffset = (scale - 1) * 150;
      setPosition({
        x: Math.min(Math.max(newX, -maxOffset), maxOffset),
        y: Math.min(Math.max(newY, -maxOffset), maxOffset),
      });
    }
  };

  const handleTouchEnd = () => {
    isDragging.current = false;
    lastDistance.current = 0;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <p className="text-slate-500">로딩 중...</p>
      </div>
    );
  }

  if (!photo) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center">
        <Image size={48} className="text-slate-300 mb-3" />
        <p className="text-slate-500">사진 정보를 찾을 수 없습니다.</p>
        <button
          onClick={() => navigate(-1)}
          className="mt-4 text-blue-500 text-sm font-medium"
        >
          목록으로 돌아가기
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 overflow-x-hidden w-full">
      <main className="w-full px-4 pt-6 pb-24">
        {/* 헤더 */}
        <header className="mb-4">
          <p className="text-xs text-gray-500">시공관리 · 시공사진</p>
          <h1 className="text-xl font-bold text-slate-900 truncate">
            {photo.title || "시공사진"}
          </h1>
        </header>

        {/* 사진 카드 */}
        <section className="mb-4 rounded-2xl overflow-hidden bg-white border border-gray-200 shadow-sm">
          {photo.file_url ? (
            <div
              onClick={openViewer}
              className="aspect-square bg-gray-100 flex items-center justify-center relative cursor-pointer active:opacity-90 transition-opacity"
            >
              <img
                src={photo.file_url}
                alt={photo.title}
                className="w-full h-full object-contain"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              <div className="hidden flex-col items-center justify-center w-full h-full">
                <Image size={48} className="text-gray-300 mb-2" />
                <p className="text-gray-400 text-sm">이미지 로드 실패</p>
              </div>
              <div className="absolute bottom-3 right-3 bg-black/50 rounded-full p-2">
                <ZoomIn size={18} className="text-white" />
              </div>
            </div>
          ) : (
            <div className="aspect-square bg-gray-100 flex flex-col items-center justify-center">
              <Image size={48} className="text-gray-300 mb-2" />
              <p className="text-gray-400 text-sm">사진 없음</p>
            </div>
          )}
        </section>

        <div className="space-y-3">
          {/* 기본 정보 섹션 */}
          <Section
            title="기본 정보"
            expanded={expandedSections.info}
            onToggle={() => toggleSection("info")}
            icon={<FileText size={16} className="text-blue-500" />}
          >
            <div className="space-y-4">
              {/* 제목 */}
              <div>
                <label className="block text-xs text-slate-500 mb-1.5">제목</label>
                {isEditing ? (
                  <input
                    type="text"
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm bg-white focus:border-blue-400 focus:outline-none"
                    placeholder="제목 입력"
                  />
                ) : (
                  <p className="text-[15px] font-medium text-slate-800">
                    {photo.title || "-"}
                  </p>
                )}
              </div>

              {/* 작성자 & 촬영일 */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-slate-50 rounded-xl p-3">
                  <div className="flex items-center gap-1.5 mb-1">
                    <User size={12} className="text-slate-400" />
                    <span className="text-[10px] text-slate-400 font-medium">작성자</span>
                  </div>
                  <p className="text-[14px] font-semibold text-slate-700">
                    {photo.created_by || "-"}
                  </p>
                </div>
                <div className="bg-slate-50 rounded-xl p-3">
                  <div className="flex items-center gap-1.5 mb-1">
                    <Calendar size={12} className="text-slate-400" />
                    <span className="text-[10px] text-slate-400 font-medium">촬영일</span>
                  </div>
                  {isEditing ? (
                    <input
                      type="date"
                      value={form.date}
                      onChange={(e) => setForm({ ...form, date: e.target.value })}
                      className="text-[14px] font-semibold text-slate-700 bg-transparent border-none outline-none p-0 w-full"
                    />
                  ) : (
                    <p className="text-[14px] font-semibold text-slate-700">
                      {formatDate(photo.date)}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </Section>

          {/* 설명 섹션 */}
          <Section
            title="설명"
            expanded={expandedSections.description}
            onToggle={() => toggleSection("description")}
            icon={<FileText size={16} className="text-green-500" />}
          >
            {isEditing ? (
              <textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm resize-none bg-white focus:border-blue-400 focus:outline-none"
                rows={4}
                placeholder="사진에 대한 설명을 입력하세요"
              />
            ) : (
              <div className="text-sm text-slate-700 whitespace-pre-line leading-relaxed">
                {photo.description || "설명 없음"}
              </div>
            )}
          </Section>
        </div>
      </main>

      {/* 모바일 하단 고정 액션바 */}
      <footer className="fixed inset-x-0 bottom-0 bg-white border-t border-gray-200 px-4 py-2.5 safe-area-bottom z-50">
        {isEditing ? (
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => setIsEditing(false)}
              className="w-full h-11 rounded-full border border-gray-300 bg-white text-gray-700 text-sm font-medium hover:bg-gray-50 active:scale-[0.97] transition-all flex items-center justify-center gap-2"
            >
              취소
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="w-full h-11 rounded-full bg-blue-500 text-white text-sm font-medium hover:bg-blue-600 active:scale-[0.97] transition-all flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <Save size={18} />
              {saving ? "저장 중..." : "저장"}
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-2">
            <button
              type="button"
              onClick={handleBack}
              className="w-full h-11 rounded-full border border-gray-300 bg-white text-gray-700 text-sm font-medium hover:bg-gray-50 active:scale-[0.97] transition-all flex items-center justify-center gap-1"
            >
              <ChevronLeft size={16} />
              목록
            </button>
            <button
              type="button"
              onClick={handleDelete}
              className="w-full h-11 rounded-full border border-red-300 bg-white text-red-500 text-sm font-medium hover:bg-red-50 active:scale-[0.97] transition-all flex items-center justify-center gap-1"
            >
              <Trash2 size={16} />
              삭제
            </button>
            <button
              type="button"
              onClick={() => setIsEditing(true)}
              className="w-full h-11 rounded-full bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 active:scale-[0.97] transition-all flex items-center justify-center gap-1"
            >
              <Edit2 size={16} />
              편집
            </button>
          </div>
        )}
      </footer>

      {isViewerOpen && (
        <div className="fixed inset-0 z-[100] bg-black flex flex-col">
          <div className="flex items-center justify-between px-4 py-3 bg-black/80">
            <button
              onClick={closeViewer}
              className="p-2 rounded-full bg-white/10 active:bg-white/20 transition-colors"
            >
              <X size={24} className="text-white" />
            </button>
            <span className="text-white text-sm font-medium">
              {Math.round(scale * 100)}%
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={handleZoomOut}
                disabled={scale <= 1}
                className="p-2 rounded-full bg-white/10 active:bg-white/20 transition-colors disabled:opacity-30"
              >
                <ZoomOut size={20} className="text-white" />
              </button>
              <button
                onClick={handleZoomIn}
                disabled={scale >= 4}
                className="p-2 rounded-full bg-white/10 active:bg-white/20 transition-colors disabled:opacity-30"
              >
                <ZoomIn size={20} className="text-white" />
              </button>
            </div>
          </div>

          <div
            ref={imageContainerRef}
            className="flex-1 flex items-center justify-center overflow-hidden touch-none"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <img
              src={photo.file_url}
              alt={photo.title}
              className="max-w-full max-h-full object-contain select-none"
              style={{
                transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
                transition: isDragging.current ? 'none' : 'transform 0.1s ease-out',
              }}
              draggable={false}
            />
          </div>

          <div className="px-4 py-3 bg-black/80 text-center">
            <p className="text-white/70 text-xs">
              두 손가락으로 확대/축소 · 한 손가락으로 이동
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

// 섹션 카드 컴포넌트
function Section({ title, expanded, onToggle, icon, children }) {
  return (
    <section className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-100"
      >
        <div className="flex items-center gap-2">
          {icon}
          <h3 className="font-semibold text-sm text-gray-800">{title}</h3>
        </div>
        <ChevronDown
          size={18}
          className={`text-gray-400 transition-transform duration-200 ${expanded ? "rotate-180" : ""
            }`}
        />
      </button>
      {expanded && <div className="p-4">{children}</div>}
    </section>
  );
}

export default Photo_view;
